import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Button, Chip,
    TextField, MenuItem, Tabs, Tab, IconButton,
    Paper, Divider, CircularProgress, Autocomplete, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
    Print as PrintIcon, Save as SaveIcon, Search as SearchIcon,
    Assignment as AssignmentIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { examAPI, classAPI, studentAPI } from '../services/api';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// ========== Number to Words (Frontend) ==========
const ones = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE',
    'TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

function numberToWords(num) {
    num = Math.floor(Number(num));
    if (isNaN(num) || num === 0) return 'ZERO';
    if (num < 0) return 'MINUS ' + numberToWords(-num);
    let words = '';
    if (Math.floor(num / 1000) > 0) {
        words += numberToWords(Math.floor(num / 1000)) + ' THOUSAND ';
        num %= 1000;
    }
    if (Math.floor(num / 100) > 0) {
        words += ones[Math.floor(num / 100)] + ' HUNDRED ';
        num %= 100;
    }
    if (num > 0) {
        if (num < 20) {
            words += ones[num];
        } else {
            words += tens[Math.floor(num / 10)];
            if (num % 10 > 0) words += ' ' + ones[num % 10];
        }
    }
    return words.trim();
}

const GRADE_OPTIONS = ['A-1', 'A-2', 'B-1', 'B-2', 'C-1', 'C-2', 'D', 'E-1', 'E-2'];
const GRADE_ROWS = [
    ["91-100","A-1"],["81-90","A-2"],["71-80","B-1"],["61-70","B-2"],
    ["51-60","C-1"],["41-50","C-2"],["33-40","D"],["21-32","E-1"],["0-20","E-2"]
];

const isGradePass = (grade) => {
    if (!grade) return true; // blank = no data yet
    const failGrades = ['E-1', 'E-2', 'E1', 'E2'];
    return !failGrades.includes(grade);
};

// ========== Format Detection ==========
const getClassNum = (name) => {
    const n = (name || '').toLowerCase();
    const match = n.match(/\d+/);
    return match ? parseInt(match[0]) : 0;
};

// ========== Format Standard Label ==========
// "Class 9" -> "9th", "Class 1" -> "1st", etc.
const formatStandard = (name) => {
    const num = getClassNum(name);
    if (!num) return name;
    const suffixes = { 1: 'st', 2: 'nd', 3: 'rd' };
    const suffix = (num >= 11 && num <= 13) ? 'th' : (suffixes[num % 10] || 'th');
    return `${num}${suffix}`;
};

// ========== Normalize Subject Names for Print ==========
const SUBJECT_NAME_MAP = {
    'SOCIAL STUDIES': 'SOCIAL SCIENCE',
    'DEFENSE STUDIES': 'DEFENCE STUDIES',
};
const normalizeSubjectName = (name) => {
    if (!name) return '';
    const upper = name.toUpperCase().trim();
    return SUBJECT_NAME_MAP[upper] || name;
};

// Format A: Grade-only report card (Classes 1-4, 6, 7)
const isFormatA = (name) => {
    const num = getClassNum(name);
    return [1, 2, 3, 4, 6, 7].includes(num);
};

// Format B: Statement of Marks (Classes 11, 12)
const isFormatB = (name) => {
    const num = getClassNum(name);
    return num >= 11;
};

// Format C: Marks + Grade hybrid (Classes 9, 10)
const isFormatC = (name) => {
    const num = getClassNum(name);
    return [9, 10].includes(num);
};

// Format D: Annual Examination Result Sheet (Classes 5, 8)
const isFormatD = (name) => {
    const num = getClassNum(name);
    return [5, 8].includes(num);
};

const ExamResultPrint = () => {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    
    const [openSummaryDialog, setOpenSummaryDialog] = useState(false);
    const [isSavingSummary, setIsSavingSummary] = useState(false);
    const [summaryData, setSummaryData] = useState({
        term1WorkingDays: '', term1PresentDays: '',
        term2WorkingDays: '', term2PresentDays: '',
        promotedToClass: '', reopenDate: 'June - 2026',
        sem1Grades: {}, sem2Grades: {},
        remarks: ''
    });

    const [exams, setExams] = useState([]);
    const [classes, setClasses] = useState([]);
    const [sections, setSections] = useState([]);
    const [students, setStudents] = useState([]);
    const [selectedExam, setSelectedExam] = useState('');
    const [selectedClass, setSelectedClass] = useState('');
    const [selectedSection, setSelectedSection] = useState('');
    const [selectedStudent, setSelectedStudent] = useState(null);

    const [subjects, setSubjects] = useState([]);
    const [marksData, setMarksData] = useState([]);
    const [resultData, setResultData] = useState(null);
    const [allResults, setAllResults] = useState([]);

    const printRef = useRef();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [examRes, classRes] = await Promise.all([
                    examAPI.getExams(),
                    classAPI.getAll()
                ]);
                setExams(examRes.data?.data || []);
                setClasses(classRes.data?.data || []);
            } catch (err) {
                toast.error('Failed to load initial data');
            }
        };
        fetchData();
    }, []);

    // Fetch sections when class changes
    useEffect(() => {
        if (selectedClass) {
            const fetchSections = async () => {
                try {
                    const res = await classAPI.getSections(selectedClass);
                    setSections(res.data?.data || []);
                } catch (err) {
                    console.error('Failed to load sections');
                }
            };
            fetchSections();
            setSelectedSection('');
            setSelectedStudent(null);
            setStudents([]);
        } else {
            setSections([]);
            setSelectedSection('');
            setStudents([]);
            setSelectedStudent(null);
        }
    }, [selectedClass]);

    // Fetch students when class + section are selected
    useEffect(() => {
        if (selectedClass && selectedSection) {
            const fetchStudents = async () => {
                try {
                    const params = { class: selectedClass, section: selectedSection, limit: 200 };
                    const res = await studentAPI.getAll(params);
                    const studentList = (res.data?.data || []).map(s => ({
                        id: s.id,
                        rollNumber: s.rollNumber || '',
                        generalRegisterNo: s.generalRegisterNo,
                        grBookId: s.grBookId,
                        fullName: `${s.user?.lastName || ''} ${s.user?.firstName || ''} ${s.user?.middleName || ''}`.replace(/\s+/g, ' ').trim(),
                        motherName: s.motherName || '',
                        uid: s.uid || '',
                    }));
                    setStudents(studentList);
                } catch (err) {
                    toast.error('Failed to load students');
                }
            };
            fetchStudents();
        } else {
            setStudents([]);
            setSelectedStudent(null);
        }
    }, [selectedClass, selectedSection]);

    const handleFetchResults = async () => {
        if (!selectedExam || !selectedClass || !selectedSection) {
            toast.warning('Please select Exam, Class and Division');
            return;
        }
        setLoading(true);
        try {
            const params = { examId: selectedExam, classId: selectedClass, sectionId: selectedSection };
            if (selectedStudent) params.studentId = selectedStudent.id;

            const res = await examAPI.getResultPrintData(params);
            const data = res.data?.data;

            if (data) {
                setResultData(data);
                setSubjects(data.subjects || []);
                setAllResults(data.students || []);

                if (selectedStudent && data.students?.length > 0) {
                    const studentResult = data.students.find(s => s.student.id === selectedStudent.id) || data.students[0];
                    const marks = (data.subjects || []).map(sub => {
                        const existing = studentResult.subjects.find(s => s.subjectId === sub.id);
                        return {
                            subjectId: sub.id,
                            subjectName: sub.name,
                            maxMarks: sub.totalMarks || 100,
                            marksObtained: existing ? (existing.marksObtained ?? '') : '',
                            grade: existing ? (existing.grade || '') : '',
                            isGradeOnly: existing ? existing.isGradeOnly : (Number(sub.totalMarks) === 0),
                        };
                    });
                    setMarksData(marks);
                }
            }
        } catch (err) {
            toast.error('Failed to fetch results');
        } finally {
            setLoading(false);
        }
    };

    const handleMarksChange = (index, field, value) => {
        setMarksData(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const handleSaveMarks = async () => {
        if (!selectedExam || !selectedStudent) return;
        const isGradeSubject = (m) => m.isGradeOnly || m.maxMarks === 'NA' || Number(m.maxMarks) === 0;
        const marks = marksData.filter(m => !isGradeSubject(m) && m.marksObtained !== '').map(m => ({
            examId: Number(selectedExam),
            studentId: selectedStudent.id,
            subjectId: m.subjectId,
            marksObtained: Number(m.marksObtained),
            totalMarks: Number(m.maxMarks),
            isPresent: true
        }));
        const gradeMarks = marksData.filter(m => isGradeSubject(m) && m.grade).map(m => ({
            examId: Number(selectedExam),
            studentId: selectedStudent.id,
            subjectId: m.subjectId,
            marksObtained: 0,
            totalMarks: 0,
            grade: m.grade,
            isPresent: true
        }));

        try {
            await examAPI.bulkUpdateMarks({ marks: [...marks, ...gradeMarks] });
            toast.success('Marks saved');
            handleFetchResults();
        } catch (err) {
            toast.error('Failed to save marks');
        }
    };

    const handleExportExcel = async () => {
        if (!allResults || allResults.length === 0) {
            toast.error('No results to export');
            return;
        }

        const className = getClassName();
        const fmtB = isFormatB(className);
        const fmtC = isFormatC(className);
        const fmtD = isFormatD(className);
        
        const exportData = allResults.map(st => {
            const row = {
                'Roll No': st.student.rollNumber || '',
                'Student Name': st.student.fullName || '',
                'GR No': st.student.grBookId || st.student.generalRegisterNo || ''
            };

            if (fmtB) {
                st.subjects.forEach(sub => {
                    const isGrade = sub.isGradeOnly || sub.maxMarks === 'NA' || Number(sub.maxMarks) === 0;
                    row[sub.subjectName] = isGrade ? (sub.grade || '-') : (sub.marksObtained || '');
                });
                row['Total Marks'] = st.summary?.totalObtained || '';
                row['Percentage'] = st.summary?.percentage ? `${st.summary.percentage}%` : '';
                row['Result'] = st.summary?.result || '';
            } else {
                let hasFail = false;
                st.subjects.forEach(sub => {
                    const key = String(sub.subjectId);
                    const isGrade = sub.isGradeOnly || sub.maxMarks === 'NA' || Number(sub.maxMarks) === 0;
                    const sem1 = st.summary?.sem1Grades?.[key] || '';
                    const sem2 = st.summary?.sem2Grades?.[key] || '';
                    
                    if ((fmtC || fmtD) && !isGrade) {
                        row[`${sub.subjectName} (Sem 1)`] = sem1;
                        row[`${sub.subjectName} (Sem 2)`] = sem2;
                        const s1 = Number(sem1) || 0;
                        const s2 = Number(sem2) || 0;
                        const avg = (sem1 || sem2) ? ((s1 + s2)/2) : 0;
                        row[`${sub.subjectName} (Avg)`] = avg > 0 ? avg.toFixed(0) : '';
                        if (avg > 0 && avg < 35) hasFail = true;
                    } else {
                        row[`${sub.subjectName} (Sem 1 Grade)`] = sem1;
                        row[`${sub.subjectName} (Sem 2 Grade)`] = sem2;
                        if (!isGradePass(sem1) || !isGradePass(sem2)) hasFail = true;
                    }
                });
                row['Result'] = hasFail ? 'Fail' : 'Pass';
            }
            return row;
        });

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Results');
        if (exportData.length > 0) {
            const headers = Object.keys(exportData[0]);
            worksheet.addRow(headers);
            exportData.forEach(row => {
                worksheet.addRow(headers.map(h => row[h]));
            });
        }
        const examName = exams.find(e => e.id === Number(selectedExam))?.name || 'Exam';
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `${className}_${examName}_Results.xlsx`.replace(/\s+/g, '_'));
    };

    // Deep parse JSON that may be multi-level stringified (from prior bug)
    const deepParseJSON = (val) => {
        if (!val) return {};
        if (typeof val === 'object' && !Array.isArray(val)) return val;
        if (typeof val === 'string') {
            try {
                let parsed = JSON.parse(val);
                // Keep parsing if still a string (double/triple stringified)
                let maxDepth = 5;
                while (typeof parsed === 'string' && maxDepth-- > 0) {
                    parsed = JSON.parse(parsed);
                }
                if (typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
                return {};
            } catch(e) { return {}; }
        }
        return {};
    };

    const handleOpenSummary = async (student) => {
        if (!student || !resultData?.exam?.academicYearId) return;
        
        const parsedStudentData = allResults.find(r => r.student.id === student.id);
        const autoSem1 = parsedStudentData?.summary?.sem1Grades || {};
        const autoSem2 = parsedStudentData?.summary?.sem2Grades || {};

        try {
            const res = await examAPI.getResultSummary({ studentId: student.id, academicYearId: resultData.exam.academicYearId });
            let dbData = res.data.data;
            
            if (dbData) {
                let s1 = deepParseJSON(dbData.sem1Grades);
                let s2 = deepParseJSON(dbData.sem2Grades);
                
                if (!s1 || Object.keys(s1).length === 0) s1 = autoSem1;
                if (!s2 || Object.keys(s2).length === 0) s2 = autoSem2;

                setSummaryData({
                    ...dbData,
                    sem1Grades: s1 || {},
                    sem2Grades: s2 || {}
                });
            } else {
                setSummaryData({
                    studentId: student.id,
                    academicYearId: resultData.exam.academicYearId,
                    term1WorkingDays: '', term1PresentDays: '',
                    term2WorkingDays: '', term2PresentDays: '',
                    promotedToClass: '', reopenDate: 'June - 2026',
                    sem1Grades: autoSem1,
                    sem2Grades: autoSem2,
                    remarks: ''
                });
            }
            setOpenSummaryDialog(true);
        } catch (error) {
            toast.error('Failed to load summary');
        }
    };

    const handleSaveSummary = async () => {
        if (!selectedStudent || !resultData?.exam?.academicYearId) return;
        setIsSavingSummary(true);
        try {
            // Only send fields the ResultSummary model accepts - strip DB metadata
            const payload = {
                studentId: selectedStudent.id,
                academicYearId: resultData.exam.academicYearId,
                term1WorkingDays: Number(summaryData.term1WorkingDays) || 0,
                term1PresentDays: Number(summaryData.term1PresentDays) || 0,
                term2WorkingDays: Number(summaryData.term2WorkingDays) || 0,
                term2PresentDays: Number(summaryData.term2PresentDays) || 0,
                promotedToClass: summaryData.promotedToClass || '',
                reopenDate: summaryData.reopenDate || 'June - 2026',
                sem1Grades: typeof summaryData.sem1Grades === 'object' ? summaryData.sem1Grades : deepParseJSON(summaryData.sem1Grades),
                sem2Grades: typeof summaryData.sem2Grades === 'object' ? summaryData.sem2Grades : deepParseJSON(summaryData.sem2Grades),
                remarks: summaryData.remarks || ''
            };
            await examAPI.saveResultSummary(payload);
            toast.success('Summary saved');
            setOpenSummaryDialog(false);
            handleFetchResults();
        } catch (error) {
            console.error('Save summary error:', error.response?.data || error);
            toast.error(error.response?.data?.message || 'Failed to save summary. Check console for details.');
        } finally {
            setIsSavingSummary(false);
        }
    };

    const handlePrint = () => {
        const printContent = printRef.current;
        if (!printContent) return;
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
            <head>
                <title>Report Card</title>
                <style>
                    @page { size: A4; margin: 4mm 6mm; }
                    * { box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
                    body { font-family: 'Times New Roman', serif; margin: 0; padding: 0; font-size: 15px; color: #000; }
                    .report-container { border: 3px double #000; padding: 10px 14px; width: 100%; min-height: 282mm; display: flex; flex-direction: column; }
                    .report-content { flex: 1; }
                    .header { text-align: center; margin-bottom: 6px; }
                    table { width: 100%; border-collapse: collapse; }
                    th, td { border: 1px solid #000; padding: 5px 6px; font-size: 15px; }
                    th { background: #f5f5f5 !important; text-align: center; font-weight: bold; font-size: 15px; }
                    .sig-box { text-align: center; border-top: 1px solid #000; width: 160px; padding-top: 5px; font-weight: bold; font-size: 14px; }
                    .label-highlight { background: #f0f4f8 !important; }
                </style>
            </head>
            <body>
                ${printContent.innerHTML}
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);
    };


    // ===========================
    // PRINT PREVIEW RENDERERS
    // ===========================
    const renderPrintPreviewTab = () => {
        const st = (selectedStudent && allResults.find(r => r.student.id === selectedStudent.id)) || allResults[0];
        if (!st) return <Box sx={{ p: 4, textAlign: 'center' }}>No data loaded.</Box>;
        
        const examInfo = resultData?.exam || {};
        const classInfo = resultData?.class || {};
        const className = classInfo.name || '';
        
        const fmtA = isFormatA(className);
        const fmtB = isFormatB(className);
        const fmtC = isFormatC(className);
        const fmtD = isFormatD(className);

        // Grade lookup helpers
        const getSem1Value = (sub) => {
            const key = String(sub.subjectId || sub.id);
            return st.summary?.sem1Grades?.[key] || '';
        };
        const getSem2Value = (sub) => {
            const key = String(sub.subjectId || sub.id);
            return st.summary?.sem2Grades?.[key] || '';
        };

        const cb = '1px solid #000';
        const cp = '8px 8px'; // Reduced vertical padding to prevent overflow at 100% scale
        const thStyle = { border: cb, padding: cp, background: '#f5f5f5', textAlign: 'center', fontWeight: 'bold', fontSize: 15 };
        const tdStyle = { border: cb, padding: cp, textAlign: 'center', fontSize: 15 };
        const tdLeft = { ...tdStyle, textAlign: 'left' };
        const labelStyle = { ...tdStyle, fontWeight: 'bold', background: '#f9f9f9' };

        // ============ FORMAT B: Higher Secondary Statement of Marks (Class 11, 12) ============
        if (fmtB) {
            let totalMax3 = 0, totalObt3 = 0;
            st.subjects.forEach(sub => {
                const m = Number(sub.marksObtained);
                const mx = Number(sub.maxMarks);
                if (mx > 0 && !isNaN(m)) { totalMax3 += mx; totalObt3 += m; }
            });
            const pct3 = totalMax3 > 0 ? ((totalObt3 / totalMax3) * 100).toFixed(2) : '0.00';
            const pctNum = Number(pct3);
            const result3 = pctNum >= 75 ? 'First Class with Distinction' : pctNum >= 60 ? 'First Class' : pctNum >= 45 ? 'Second Class' : pctNum >= 33 ? 'Pass' : 'Fail';
            const remark3 = pctNum >= 75 ? 'FIRST CLASS WITH DISTINCTION' : pctNum >= 60 ? 'FIRST CLASS' : pctNum >= 45 ? 'SECOND CLASS' : pctNum >= 33 ? 'PASS CLASS' : 'FAIL';

            const classNum = (classInfo.name || '').match(/\d+/)?.[0] || '';
            const stdLabel = `STD-${classNum === '11' ? 'XI' : classNum === '12' ? 'XII' : classNum}`;

            // Default grade A for Environmental Education and Physical Education in class 11
            const fmtBSubjects = st.subjects.map(sub => {
                const isGrade = sub.isGradeOnly || sub.maxMarks === 'NA' || Number(sub.maxMarks) === 0;
                const nameUpper = (sub.subjectName || '').toUpperCase();
                if (isGrade && classNum === '11' && (nameUpper.includes('ENVIRONMENT') || nameUpper.includes('PHYSICAL EDUCATION'))) {
                    return { ...sub, grade: 'A' };
                }
                return sub;
            });

            return (
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>Print Statement of Marks</Button>
                    </Box>
                    <Paper ref={printRef} sx={{ p: 0, width: '100%', maxWidth: '210mm', mx: 'auto', bgcolor: '#fff', color: '#000', overflow: 'hidden' }}>
                        <div className="report-container" style={{ border: '2px solid #000', padding: '18px 24px', fontFamily: "'Times New Roman', serif", fontSize: 11, color: '#000', minHeight: '277mm', display: 'flex', flexDirection: 'column' }}>
                            <div className="report-content" style={{ flex: 1 }}>
                            <div style={{ textAlign: 'center', marginBottom: 10 }}>
                                <img src="/logo1.png" alt="Logo" style={{ height: 65, marginBottom: 4 }} />
                                <div style={{ fontSize: 18, fontWeight: 'bold' }}>Vidya Pratishthan's</div>
                                <div style={{ fontSize: 22, fontWeight: 'bold', letterSpacing: 1 }}>Supe English Medium School</div>
                                <div style={{ fontSize: 13 }}>Supe, Tal - Baramati, Dist - Pune</div>
                                <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 4 }}>Higher Secondary School Examination</div>
                                <div style={{ fontSize: 16, fontWeight: 'bold', textDecoration: 'underline', letterSpacing: 2 }}>STATEMENT OF MARKS</div>
                            </div>
                            <div style={{ textAlign: 'center', marginBottom: 2 }}>
                                <div style={{ fontSize: 15, fontWeight: 'bold' }}>
                                    {stdLabel}&nbsp;&nbsp;Science-&nbsp;Div [{st.student.sectionName || 'A'}]
                                </div>
                                <div style={{ fontSize: 14, fontWeight: 'bold', marginTop: 2 }}>
                                    Academic Year 2025-2026
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 40, margin: '8px 0', fontSize: 15 }}>
                                <div><b>Roll No.:</b>&nbsp;&nbsp;&nbsp;&nbsp;{st.student.rollNumber}</div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', margin: '4px 0 12px', fontSize: 15 }}>
                                <div><b>Student's Name:</b>&nbsp;&nbsp;<b>{st.student.fullName}</b></div>
                                <div><b>Mother's Name:</b>&nbsp;&nbsp;{st.student.motherName}</div>
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
                                <thead>
                                    <tr><th style={thStyle} rowSpan={2}>Sr. No.</th><th style={thStyle} rowSpan={2}>Subject</th><th style={thStyle} rowSpan={2}>Max. Marks</th><th style={thStyle} colSpan={2}>Marks or Grade Obtained</th></tr>
                                    <tr><th style={thStyle}>In Figures</th><th style={thStyle}>In Words</th></tr>
                                </thead>
                                <tbody>
                                    {fmtBSubjects.map((sub, i) => {
                                        const isGrade = sub.isGradeOnly || sub.maxMarks === 'NA' || Number(sub.maxMarks) === 0;
                                        const marks = Number(sub.marksObtained);
                                        return (
                                            <tr key={i}>
                                                <td style={tdStyle}>{i + 1}</td>
                                                <td style={tdLeft}>{normalizeSubjectName(sub.subjectName).toUpperCase()}</td>
                                                <td style={tdStyle}>{isGrade ? <i>Grade</i> : sub.maxMarks}</td>
                                                <td style={tdStyle}>{isGrade ? (sub.grade || '-') : (isNaN(marks) ? '' : marks)}</td>
                                                <td style={tdLeft}>{isGrade ? '-' : (isNaN(marks) ? '' : numberToWords(marks))}</td>
                                            </tr>
                                        );
                                    })}
                                    <tr>
                                        <td style={{ ...tdStyle, fontWeight: 'bold' }}>Percentage</td>
                                        <td style={{ ...tdStyle, fontWeight: 'bold' }}>{pct3}%</td>
                                        <td style={{ ...tdStyle, fontWeight: 'bold' }}>Total Marks&nbsp;&nbsp;{totalMax3}</td>
                                        <td style={{ ...tdStyle, fontWeight: 'bold' }}>{totalObt3}</td>
                                        <td style={{ ...tdLeft, fontWeight: 'bold' }}>{numberToWords(totalObt3)}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ ...tdStyle, fontWeight: 'bold' }}>Remark</td>
                                        <td colSpan={2} style={{ ...tdLeft, fontWeight: 'bold' }}>{remark3}</td>
                                        <td colSpan={2} style={{ ...tdStyle, fontWeight: 'bold' }}>Result: {result3}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div style={{ fontSize: 15, fontWeight: 'bold', marginTop: 10, marginBottom: 6 }}>
                                Promoted to class - {classNum === '11' ? '12' : (classNum === '12' ? 'Degree' : '')}
                            </div>
                            <div style={{ fontSize: 11, marginTop: 4, lineHeight: 1.6 }}>
                                <b>Note :</b><br />
                                1. Minimum Passing marks required are 35 out of 100.<br />
                                2. Grades : First Class with Distinction - 75% and above,<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                First Class - 60% to 74.99%, Second Class - 45% to 59.99%,<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                Pass Class - &lt; 45%<br />
                                3. * - Deficiency in subject, # - Gracing for First Class and Distinction grade
                            </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 'auto', paddingTop: 20 }}>
                                <div style={{ fontSize: 15, fontWeight: 'bold' }}>Date :&nbsp;&nbsp;30/04/2026</div>
                                <div style={{ textAlign: 'center' }}><div style={{ borderTop: '1px solid #000', paddingTop: 3, fontWeight: 'bold', fontSize: 15, width: 150 }}>Class Teacher</div></div>
                                <div style={{ textAlign: 'center' }}><div style={{ borderTop: '1px solid #000', paddingTop: 3, fontWeight: 'bold', fontSize: 15, width: 150 }}>Principal</div></div>
                            </div>
                        </div>
                    </Paper>
                </Box>
            );
        }

        // ============ RIGHT PANEL (shared by Format A and Format C) ============
        const renderRightPanel = (resultText, percentageVal) => (
            <td style={{ width: '42%', verticalAlign: 'top', paddingLeft: 0, border: '1px solid #000' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0 }}>
                    <tbody>
                        <tr><td style={{ ...labelStyle, borderTop: 'none', borderLeft: 'none', borderRight: 'none' }}>Result</td><td style={{ ...tdStyle, fontWeight: 800, borderTop: 'none', borderRight: 'none' }}>{resultText}</td></tr>
                        {percentageVal !== undefined && <tr><td style={{ ...labelStyle, borderLeft: 'none', borderRight: 'none' }}>Percentage</td><td style={{ ...tdStyle, fontWeight: 800, borderRight: 'none' }}>{percentageVal}%</td></tr>}
                    </tbody>
                </table>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0 }}>
                    <thead>
                        <tr><th colSpan={3} style={{ ...thStyle, borderLeft: 'none', borderRight: 'none' }}>Attendance</th></tr>
                        <tr><th style={{ ...thStyle, borderLeft: 'none' }}>Term</th><th style={thStyle}>Working Days</th><th style={{ ...thStyle, borderRight: 'none' }}>Present Days</th></tr>
                    </thead>
                    <tbody>
                        <tr><td style={{ ...labelStyle, borderLeft: 'none' }}>Term - I</td><td style={tdStyle}>{st.summary?.attendance?.t1Working || ''}</td><td style={{ ...tdStyle, borderRight: 'none' }}>{st.summary?.attendance?.t1Present || ''}</td></tr>
                        <tr><td style={{ ...labelStyle, borderLeft: 'none' }}>Term - II</td><td style={tdStyle}>{st.summary?.attendance?.t2Working || ''}</td><td style={{ ...tdStyle, borderRight: 'none' }}>{st.summary?.attendance?.t2Present || ''}</td></tr>
                    </tbody>
                </table>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr><th colSpan={2} style={{ ...thStyle, borderLeft: 'none', borderRight: 'none' }}>Grades Description</th></tr>
                        <tr><th style={{ ...thStyle, borderLeft: 'none' }}>Percentage</th><th style={{ ...thStyle, borderRight: 'none' }}>Grade</th></tr>
                    </thead>
                    <tbody>
                        {GRADE_ROWS.map(([p, g]) => (
                            <tr key={p}><td style={{ ...tdStyle, borderLeft: 'none' }}>{p}</td><td style={{ ...tdStyle, fontWeight: 'bold', borderRight: 'none' }}>{g}</td></tr>
                        ))}
                    </tbody>
                </table>
            </td>
        );

        // ============ HEADER (shared by Format A and Format C) ============
        const renderHeader = () => (
            <div>
                <div className="header" style={{ textAlign: 'center', marginBottom: '6px' }}>
                    <img src="/logo1.png" alt="Logo" style={{ height: 55, marginBottom: 3 }} />
                    <div style={{ fontSize: 17, fontWeight: 'bold' }}>Vidya Pratishthan's</div>
                    <div style={{ fontSize: 21, fontWeight: 'bold' }}>Supe English Medium School</div>
                    <div style={{ fontSize: 12 }}>Supe, Tal. - Baramati Dist. - Pune</div>
                    <div style={{ fontSize: 11 }}>UDISE – 27250200605 Ph. No. 7796686110</div>
                    <div style={{ fontSize: 15, fontWeight: 'bold', textDecoration: 'underline', margin: '4px 0 2px' }}>Report Card</div>
                    <div style={{ fontSize: 14, fontWeight: 'bold' }}>Academic Year: 2025-2026</div>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 4 }}>
                    <tbody>
                        <tr>
                            <td style={{ border: cb, padding: cp, fontWeight: 'bold', background: '#f0f4f8', width: '25%', fontSize: 15 }}>Student Name</td>
                            <td style={{ border: cb, padding: cp, fontWeight: 700, width: '25%', fontSize: 15 }}>{st.student.fullName}</td>
                            <td style={{ border: cb, padding: cp, fontWeight: 'bold', background: '#f0f4f8', width: '25%', fontSize: 15 }}>Standard</td>
                            <td style={{ border: cb, padding: cp, fontWeight: 700, width: '25%', fontSize: 15 }}>{formatStandard(classInfo.name)}</td>
                        </tr>
                        <tr>
                            <td style={{ border: cb, padding: cp, fontWeight: 'bold', background: '#f0f4f8', fontSize: 15 }}>Mother Name</td>
                            <td style={{ border: cb, padding: cp, fontWeight: 700, fontSize: 15 }}>{st.student.motherName}</td>
                            <td style={{ border: cb, padding: cp, fontWeight: 'bold', background: '#f0f4f8', fontSize: 15 }}>Division</td>
                            <td style={{ border: cb, padding: cp, fontWeight: 700, fontSize: 15 }}>{st.student.sectionName}</td>
                        </tr>
                        <tr>
                            <td style={{ border: cb, padding: cp, fontWeight: 'bold', background: '#f0f4f8', fontSize: 15 }}>GR. No.</td>
                            <td style={{ border: cb, padding: cp, fontWeight: 700, fontSize: 15 }}>{st.student.grBookId || st.student.generalRegisterNo}</td>
                            <td style={{ border: cb, padding: cp, fontWeight: 'bold', background: '#f0f4f8', fontSize: 15 }}>Roll No.</td>
                            <td style={{ border: cb, padding: cp, fontWeight: 700, fontSize: 15 }}>{st.student.rollNumber}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );

        // ============ FOOTER (shared by Format A and Format C) ============
        const renderFooter = () => (
            <div style={{ paddingTop: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, padding: '0 4px' }}>
                    <div>
                        <div style={{ fontWeight: 'bold' }}>Promoted to class - {st.summary?.promotedTo}</div>
                        <div style={{ fontWeight: 'bold', marginTop: 2 }}>School will reopen on &nbsp;&nbsp;&nbsp; 15th June 2026</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 'bold' }}>Date - 30/04/2026</div>
                    </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 40, padding: '0 20px' }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ borderTop: '1px solid #000', width: 160, paddingTop: 5, fontWeight: 'bold', fontSize: 15 }}>Class Teacher</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ borderTop: '1px solid #000', width: 160, paddingTop: 5, fontWeight: 'bold', fontSize: 15 }}>Principal</div>
                    </div>
                </div>
            </div>
        );

        // ============ FORMAT A: Grade-Only Report Card (Classes 1-4, 6, 7) ============
        if (fmtA) {
            // Determine result: Pass if ALL grades >= D, Fail if ANY E-1 or E-2
            let allPass = true;
            st.subjects.forEach(sub => {
                const g1 = getSem1Value(sub);
                const g2 = getSem2Value(sub);
                if (!isGradePass(g1) || !isGradePass(g2)) allPass = false;
            });
            const resultText = allPass ? 'Pass' : 'Fail';

            const rightRowCount = 16;
            const leftContentRows = st.subjects.length + 1;
            const emptyRowsNeeded = Math.max(0, rightRowCount - leftContentRows);

            return (
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>Print Report Card</Button>
                    </Box>
                    <Paper ref={printRef} sx={{ p: 0, width: '100%', maxWidth: '210mm', mx: 'auto', bgcolor: '#fff', color: '#000', overflow: 'hidden' }}>
                        <div className="report-container" style={{ border: '3px double #000', padding: '10px 14px', fontFamily: "'Times New Roman', serif", fontSize: '15px', color: '#000', minHeight: '282mm', display: 'flex', flexDirection: 'column' }}>
                          <div className="report-content" style={{ flex: 1 }}>
                            {renderHeader()}
                            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                                <div style={{ width: '58%', border: '1px solid #000' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead><tr>
                                                <th style={{ ...thStyle, width: 30, borderTop: 'none', borderLeft: 'none', fontSize: 17 }}>Sr. No.</th>
                                                <th style={{ ...thStyle, borderTop: 'none', fontSize: 17 }}>Subject</th>
                                                <th style={{ ...thStyle, borderTop: 'none', fontSize: 17 }}>First Semester Grade</th>
                                                <th style={{ ...thStyle, borderTop: 'none', borderRight: 'none', fontSize: 17 }}>Second Semester Grade</th>
                                            </tr></thead>
                                            <tbody>
                                                {st.subjects.map((sub, i) => (
                                                    <tr key={i}>
                                                        <td style={{ ...tdStyle, borderLeft: 'none', fontSize: 17 }}>{i + 1}</td>
                                                        <td style={{ ...tdLeft, fontSize: 17 }}>{normalizeSubjectName(sub.subjectName).toUpperCase()}</td>
                                                        <td style={{ ...tdStyle, fontSize: 17 }}>{getSem1Value(sub)}</td>
                                                        <td style={{ ...tdStyle, borderRight: 'none', fontSize: 17 }}>{getSem2Value(sub)}</td>
                                                    </tr>
                                                ))}
                                                <tr>
                                                    <td colSpan={4} style={{ borderTop: 'none', borderLeft: 'none', borderRight: 'none', borderBottom: 'none', padding: '30px 20px', textAlign: 'center', fontStyle: 'italic', fontWeight: 'bold', fontSize: 14, color: '#333' }}>
                                                        "Congratulations on your hard work! Keep striving for excellence and reaching for the stars. Wishing you great success in your future endeavors."
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div style={{ width: '42%', border: '1px solid #000' }}>
                                        {(() => {
                                            // Inline right panel for Format A
                                            return <>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0 }}>
                                            <tbody><tr><td style={{ ...labelStyle, borderTop: 'none', borderLeft: 'none', borderRight: 'none' }}>Result</td><td style={{ ...tdStyle, fontWeight: 800, borderTop: 'none', borderRight: 'none' }}>{resultText}</td></tr></tbody>
                                        </table>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0 }}>
                                            <thead>
                                                <tr><th colSpan={3} style={{ ...thStyle, borderLeft: 'none', borderRight: 'none' }}>Attendance</th></tr>
                                                <tr><th style={{ ...thStyle, borderLeft: 'none' }}>Term</th><th style={thStyle}>Working Days</th><th style={{ ...thStyle, borderRight: 'none' }}>Present Days</th></tr>
                                            </thead>
                                            <tbody>
                                                <tr><td style={{ ...labelStyle, borderLeft: 'none' }}>Term - I</td><td style={tdStyle}>{st.summary?.attendance?.t1Working || ''}</td><td style={{ ...tdStyle, borderRight: 'none' }}>{st.summary?.attendance?.t1Present || ''}</td></tr>
                                                <tr><td style={{ ...labelStyle, borderLeft: 'none' }}>Term - II</td><td style={tdStyle}>{st.summary?.attendance?.t2Working || ''}</td><td style={{ ...tdStyle, borderRight: 'none' }}>{st.summary?.attendance?.t2Present || ''}</td></tr>
                                            </tbody>
                                        </table>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr><th colSpan={2} style={{ ...thStyle, borderLeft: 'none', borderRight: 'none' }}>Grades Description</th></tr>
                                                <tr><th style={{ ...thStyle, borderLeft: 'none' }}>Percentage</th><th style={{ ...thStyle, borderRight: 'none' }}>Grade</th></tr>
                                            </thead>
                                            <tbody>
                                                {GRADE_ROWS.map(([p, g]) => (
                                                    <tr key={p}><td style={{ ...tdStyle, borderLeft: 'none' }}>{p}</td><td style={{ ...tdStyle, fontWeight: 'bold', borderRight: 'none' }}>{g}</td></tr>
                                                ))}
                                            </tbody>
                                        </table>
                                            </>;
                                        })()}
                                    </div>
                                </div>
                          </div>
                            {renderFooter()}
                        </div>
                    </Paper>
                </Box>
            );
        }

        // ============ FORMAT C: Marks + Grade Hybrid (Classes 5, 8, 9, 10) ============
        if (fmtC) {
            // For numeric subjects, calculate average = (sem1 + sem2) / 2
            // Total = sum of all averages (numeric subjects only)
            let totalAvg = 0;
            let hasFail = false;
            const subjectRows = st.subjects.map((sub, i) => {
                const isGrade = sub.isGradeOnly || sub.maxMarks === 'NA' || Number(sub.maxMarks) === 0;
                const sem1Val = getSem1Value(sub);
                const sem2Val = getSem2Value(sub);
                
                if (isGrade) {
                    // Grade-only subjects: force grade to 'A' for 9th class
                    return { ...sub, isGrade, sem1: 'A', sem2: 'A', avg: '' };
                } else {
                    const s1 = Number(sem1Val) || 0;
                    const s2 = Number(sem2Val) || 0;
                    const avg = (s1 + s2) / 2;
                    totalAvg += avg;
                    if (avg < 35) hasFail = true;
                    return { ...sub, isGrade, sem1: sem1Val, sem2: sem2Val, avg: avg > 0 ? avg.toFixed(0) : '' };
                }
            });

            const pct = totalAvg > 0 ? ((totalAvg / (subjectRows.filter(s => !s.isGrade).length * 100)) * 100).toFixed(2) : '0.00';
            const pctNum = Number(pct);
            // For classes 1-9: result is only Pass/Fail. For 10+: detailed result.
            const classNumC = getClassNum(className);
            let resultText = 'Pass';
            if (hasFail) {
                resultText = 'Fail';
            } else if (classNumC >= 10) {
                if (pctNum >= 75) resultText = 'First Class with Distinction';
                else if (pctNum >= 60) resultText = 'First Class';
                else if (pctNum >= 45) resultText = 'Second Class';
                else resultText = 'Pass';
            }

            const rightRowCount = 16;
            const leftContentRows = st.subjects.length + 2;
            const emptyRowsNeeded = Math.max(0, rightRowCount - leftContentRows);

            return (
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>Print Report Card</Button>
                    </Box>
                    <Paper ref={printRef} sx={{ p: 0, width: '100%', maxWidth: '210mm', mx: 'auto', bgcolor: '#fff', color: '#000', overflow: 'hidden' }}>
                        <div className="report-container" style={{ border: '3px double #000', padding: '10px 14px', fontFamily: "'Times New Roman', serif", fontSize: '15px', color: '#000', minHeight: '282mm', display: 'flex', flexDirection: 'column' }}>
                          <div className="report-content" style={{ flex: 1 }}>
                            {renderHeader()}
                            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 6 }}>
                                <tbody><tr>
                                    <td style={{ width: '58%', verticalAlign: 'top', padding: 0, border: '1px solid #000' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead><tr>
                                                <th style={{ ...thStyle, width: 30, borderTop: 'none', borderLeft: 'none' }}>Sr. No.</th>
                                                <th style={{ ...thStyle, borderTop: 'none' }}>Subject</th>
                                                <th style={{ ...thStyle, borderTop: 'none' }}>First Semester Marks</th>
                                                <th style={{ ...thStyle, borderTop: 'none' }}>Second Semester Marks</th>
                                                <th style={{ ...thStyle, borderTop: 'none', borderRight: 'none' }}>Average</th>
                                            </tr></thead>
                                            <tbody>
                                                {subjectRows.map((sub, i) => (
                                                    <tr key={i}>
                                                        <td style={{ ...tdStyle, borderLeft: 'none' }}>{i + 1}</td>
                                                        <td style={tdLeft}>{normalizeSubjectName(sub.subjectName).toUpperCase()}</td>
                                                        <td style={tdStyle}>{sub.sem1 || ''}</td>
                                                        <td style={tdStyle}>{sub.sem2 || ''}</td>
                                                        <td style={{ ...tdStyle, borderRight: 'none' }}>{sub.isGrade ? '-' : sub.avg}</td>
                                                    </tr>
                                                ))}
                                                <tr>
                                                    <td colSpan={2} style={{ ...labelStyle, textAlign: 'left', fontWeight: 'bold', borderLeft: 'none', borderBottom: 'none' }}>Total</td>
                                                    <td style={{ ...tdStyle, fontWeight: 'bold', borderBottom: 'none' }}>{(() => { let t = 0; subjectRows.forEach(s => { if (!s.isGrade) t += (Number(s.sem1) || 0); }); return t > 0 ? t : ''; })()}</td>
                                                    <td style={{ ...tdStyle, fontWeight: 'bold', borderBottom: 'none' }}>{(() => { let t = 0; subjectRows.forEach(s => { if (!s.isGrade) t += (Number(s.sem2) || 0); }); return t > 0 ? t : ''; })()}</td>
                                                    <td style={{ ...tdStyle, fontWeight: 'bold', borderRight: 'none', borderBottom: 'none' }}>{totalAvg > 0 ? totalAvg.toFixed(0) : ''}</td>
                                                </tr>
                                                <tr>
                                                    <td colSpan={5} style={{ borderTop: '1px solid #000', borderLeft: 'none', borderRight: 'none', borderBottom: 'none', padding: '15px 10px', textAlign: 'center', fontStyle: 'italic', fontWeight: 'bold', fontSize: 13, color: '#333' }}>
                                                        "Congratulations on your hard work! Keep striving for excellence and reaching for the stars. Wishing you great success in your future endeavors."
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </td>
                                    {renderRightPanel(resultText, pct)}
                                </tr></tbody>
                            </table>
                          </div>
                            {renderFooter()}
                        </div>
                    </Paper>
                </Box>
            );
        }

        // ============ FORMAT D: Annual Examination Result Sheet (Classes 5, 8) ============
        // For 5th and 8th class: use ONLY Semester 2 marks (no Sem 1, no Average)
        if (fmtD) {
            let totalObt = 0;
            let hasFail = false;
            
            const subjectRows = st.subjects.map((sub, i) => {
                const isGrade = sub.isGradeOnly || sub.maxMarks === 'NA' || Number(sub.maxMarks) === 0;
                const sem2Val = getSem2Value(sub);
                
                if (isGrade) {
                    if (!isGradePass(sem2Val)) hasFail = true;
                    return { ...sub, isGrade, obt: sem2Val || '', minR: '-', tot: '-' };
                } else {
                    const s2 = Number(sem2Val) || 0;
                    totalObt += s2;
                    const subjectMax = Number(sub.maxMarks) || Number(sub.totalMarks) || 100;
                    
                    let passMark = Number(sub.passingMarks) || 35;
                    if (className.includes('5')) passMark = 18;
                    if (className.includes('8')) passMark = 21;

                    if (s2 > 0 && s2 < passMark) hasFail = true;
                    return { 
                        ...sub, isGrade, 
                        obt: sem2Val !== '' && sem2Val !== undefined && sem2Val !== null ? sem2Val.toString() : '',
                        minR: passMark,
                        tot: subjectMax
                    };
                }
            });

            const maxTotalPossible = subjectRows.filter(s => !s.isGrade).reduce((sum, s) => sum + Number(s.tot), 0);
            const pct = maxTotalPossible > 0 ? ((totalObt / maxTotalPossible) * 100).toFixed(2) : '0.00';
            const pctNum = Number(pct);
            // For classes 1-9: result is only Pass/Fail
            const classNumD = getClassNum(className);
            let resultText = 'Pass';
            if (hasFail) resultText = 'Fail';
            else if (classNumD >= 10) {
                if (pctNum >= 75) resultText = 'First Class with Distinction';
                else if (pctNum >= 60) resultText = 'First Class';
                else if (pctNum >= 45) resultText = 'Second Class';
            }

            return (
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>Print Report Card</Button>
                    </Box>
                    <Paper ref={printRef} sx={{ p: 0, width: '100%', maxWidth: '210mm', mx: 'auto', bgcolor: '#fff', color: '#000', overflow: 'hidden' }}>
                        <div className="report-container" style={{ border: '3px double #000', padding: '10px 14px', fontFamily: "'Times New Roman', serif", fontSize: '15px', color: '#000', minHeight: '282mm', display: 'flex', flexDirection: 'column' }}>
                          <div className="report-content" style={{ flex: 1 }}>
                            <div style={{ textAlign: 'center', marginBottom: '6px' }}>
                                <img src="/logo1.png" alt="Logo" style={{ height: 55, marginBottom: 3 }} />
                                <div style={{ fontSize: 17, fontWeight: 'bold' }}>Vidya Pratishthan's</div>
                                <div style={{ fontSize: 21, fontWeight: 'bold' }}>Supe English Medium School</div>
                                <div style={{ fontSize: 12 }}>Supe, Tal. - Baramati Dist. - Pune</div>
                                <div style={{ fontSize: 11 }}>UDISE – 27250200605</div>
                                <div style={{ fontSize: 14, fontWeight: 'bold', margin: '4px 0 2px', borderTop: '1px solid #000', borderBottom: '1px solid #000', padding: '2px 0' }}>
                                    Annual Examination/Re-Examination Result Sheet
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 'bold' }}>Academic Year: 2025-2026</div>
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 6 }}>
                                <tbody>
                                    <tr>
                                        <td style={{ ...labelStyle, width: '20%', background: '#f0f4f8', fontSize: 15 }}>Student Name</td>
                                        <td style={{ ...tdLeft, width: '45%', fontSize: 15, fontWeight: 700 }} colSpan={2}>{st.student.fullName}</td>
                                        <td style={{ ...labelStyle, width: '15%', background: '#f0f4f8', fontSize: 15 }}>Standard</td>
                                        <td style={{ ...tdStyle, width: '20%', fontSize: 15, fontWeight: 700 }}>{formatStandard(classInfo.name)}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ ...labelStyle, background: '#f0f4f8', fontSize: 15 }}>Mother Name</td>
                                        <td style={{ ...tdLeft, fontSize: 15, fontWeight: 700 }} colSpan={2}>{st.student.motherName}</td>
                                        <td style={{ ...labelStyle, background: '#f0f4f8', fontSize: 15 }}>Division</td>
                                        <td style={{ ...tdStyle, fontSize: 15, fontWeight: 700 }}>{st.student.sectionName}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ ...labelStyle, background: '#f0f4f8', fontSize: 15 }}>G.R. No.</td>
                                        <td style={{ ...tdLeft, fontSize: 15, fontWeight: 700 }} colSpan={2}>{st.student.grBookId || st.student.generalRegisterNo}</td>
                                        <td style={{ ...labelStyle, background: '#f0f4f8', fontSize: 15 }}>Roll No.</td>
                                        <td style={{ ...tdStyle, fontSize: 15, fontWeight: 700 }}>{st.student.rollNumber}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                                <div style={{ width: '62%', border: '1px solid #000' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                            <colgroup>
                                                <col style={{ width: '6%' }} />
                                                <col style={{ width: '28%' }} />
                                                <col style={{ width: '14%' }} />
                                                <col style={{ width: '10%' }} />
                                                <col style={{ width: '20%' }} />
                                                <col style={{ width: '14%' }} />
                                            </colgroup>
                                            <thead><tr>
                                                <th style={{ ...thStyle, borderTop: 'none', borderLeft: 'none', fontSize: 15, padding: '5px 3px' }}>Sr.</th>
                                                <th style={{ ...thStyle, borderTop: 'none', fontSize: 15, padding: '5px 3px' }}>Subject</th>
                                                <th style={{ ...thStyle, borderTop: 'none', fontSize: 15, padding: '5px 3px' }}>Minimum Required Marks</th>
                                                <th style={{ ...thStyle, borderTop: 'none', fontSize: 15, padding: '5px 3px' }}>Total marks</th>
                                                <th style={{ ...thStyle, borderTop: 'none', fontSize: 15, padding: '5px 3px' }}>Obtained marks/Grade</th>
                                                <th style={{ ...thStyle, borderTop: 'none', borderRight: 'none', fontSize: 15, padding: '5px 3px' }}>Remark</th>
                                            </tr></thead>
                                            <tbody>
                                                {subjectRows.map((sub, i) => (
                                                    <tr key={i}>
                                                        <td style={{ ...tdStyle, borderLeft: 'none', fontSize: 15, padding: '5px 3px' }}>{i + 1}</td>
                                                        <td style={{ ...tdLeft, fontSize: 15, padding: '5px 4px' }}>{normalizeSubjectName(sub.subjectName).toUpperCase()}</td>
                                                        <td style={{ ...tdStyle, fontSize: 15, padding: '5px 3px' }}>{sub.minR}</td>
                                                        <td style={{ ...tdStyle, fontSize: 15, padding: '5px 3px' }}>{sub.tot}</td>
                                                        <td style={{ ...tdStyle, fontWeight: 'bold', fontSize: 15, padding: '5px 3px' }}>{sub.obt}</td>
                                                        <td style={{ ...tdStyle, borderRight: 'none', fontSize: 15, padding: '5px 3px' }}>{sub.obt === '' ? '' : (sub.isGrade ? '-' : (Number(sub.obt) < Number(sub.minR) ? 'Fail' : 'Pass'))}</td>
                                                    </tr>
                                                ))}
                                                <tr>
                                                    <td colSpan={3} style={{ ...labelStyle, textAlign: 'right', borderLeft: 'none', borderBottom: 'none' }}>Total</td>
                                                    <td style={{ ...tdStyle, borderBottom: 'none' }}>{maxTotalPossible || ''}</td>
                                                    <td style={{ ...tdStyle, fontWeight: 'bold', borderBottom: 'none' }}>{totalObt > 0 ? totalObt.toFixed(0) : ''}</td>
                                                    <td style={{ ...tdStyle, borderRight: 'none', borderBottom: 'none' }}></td>
                                                </tr>
                                                <tr>
                                                    <td colSpan={6} style={{ borderTop: '1px solid #000', borderLeft: 'none', borderRight: 'none', borderBottom: 'none', padding: '15px 10px', textAlign: 'center', fontStyle: 'italic', fontWeight: 'bold', fontSize: 13, color: '#333' }}>
                                                        "Congratulations on your hard work! Keep striving for excellence and reaching for the stars. Wishing you great success in your future endeavors."
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div style={{ width: '38%', border: '1px solid #000' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0 }}>
                                            <tbody>
                                                <tr><td style={{ ...labelStyle, borderTop: 'none', borderLeft: 'none', borderRight: 'none' }}>Result</td><td style={{ ...tdStyle, fontWeight: 800, borderTop: 'none', borderRight: 'none' }}>{resultText}</td></tr>
                                                <tr><td style={{ ...labelStyle, borderLeft: 'none', borderRight: 'none' }}>Percentage</td><td style={{ ...tdStyle, fontWeight: 800, borderRight: 'none' }}>{pct}%</td></tr>
                                            </tbody>
                                        </table>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0 }}>
                                            <thead>
                                                <tr><th colSpan={3} style={{ ...thStyle, borderLeft: 'none', borderRight: 'none' }}>Attendance</th></tr>
                                                <tr><th style={{ ...thStyle, borderLeft: 'none' }}>Term</th><th style={thStyle}>Working Days</th><th style={{ ...thStyle, borderRight: 'none' }}>Present Days</th></tr>
                                            </thead>
                                            <tbody>
                                                <tr><td style={{ ...labelStyle, borderLeft: 'none' }}>Term - I</td><td style={tdStyle}>{st.summary?.attendance?.t1Working || ''}</td><td style={{ ...tdStyle, borderRight: 'none' }}>{st.summary?.attendance?.t1Present || ''}</td></tr>
                                                <tr><td style={{ ...labelStyle, borderLeft: 'none' }}>Term - II</td><td style={tdStyle}>{st.summary?.attendance?.t2Working || ''}</td><td style={{ ...tdStyle, borderRight: 'none' }}>{st.summary?.attendance?.t2Present || ''}</td></tr>
                                            </tbody>
                                        </table>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr><th colSpan={2} style={{ ...thStyle, borderLeft: 'none', borderRight: 'none' }}>Grades Description</th></tr>
                                                <tr><th style={{ ...thStyle, borderLeft: 'none' }}>Percentage</th><th style={{ ...thStyle, borderRight: 'none' }}>Grade</th></tr>
                                            </thead>
                                            <tbody>
                                                {GRADE_ROWS.map(([p, g]) => (
                                                    <tr key={p}><td style={{ ...tdStyle, borderLeft: 'none' }}>{p}</td><td style={{ ...tdStyle, fontWeight: 'bold', borderRight: 'none' }}>{g}</td></tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                          </div>
                          {renderFooter()}
                        </div>
                    </Paper>
                </Box>
            );
        }

        // Fallback (shouldn't happen)
        return <Box sx={{ p: 4, textAlign: 'center' }}>Unknown class format.</Box>;
    };

    // ===========================
    // SUMMARY DIALOG - dynamic based on class format
    // ===========================
    const getClassName = () => {
        if (!selectedClass) return '';
        const cls = classes.find(c => c.id === Number(selectedClass) || c.id === selectedClass);
        return cls?.name || '';
    };

    const renderSummaryGradesSection = () => {
        const className = getClassName();
        const fmtA = isFormatA(className);
        const fmtC = isFormatC(className);
        const fmtD = isFormatD(className);

        if (fmtA) {
            // Format A: All subjects get Sem1 Grade + Sem2 Grade dropdowns
            return (
                <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom fontWeight={700}>Subject-wise Semester Grades</Typography>
                    <Table size="small">
                        <TableHead><TableRow><TableCell>Subject</TableCell><TableCell align="center">Sem 1 Grade</TableCell><TableCell align="center">Sem 2 Grade</TableCell></TableRow></TableHead>
                        <TableBody>
                            {subjects.map(s => (
                                <TableRow key={s.id}>
                                    <TableCell>{s.name}</TableCell>
                                    <TableCell align="center">
                                        <TextField select size="small" sx={{ width: 90 }} value={summaryData.sem1Grades?.[s.id] || ''} onChange={(e) => setSummaryData({...summaryData, sem1Grades: {...summaryData.sem1Grades, [s.id]: e.target.value}})}>
                                            <MenuItem value="">-</MenuItem>
                                            {GRADE_OPTIONS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                                        </TextField>
                                    </TableCell>
                                    <TableCell align="center">
                                        <TextField select size="small" sx={{ width: 90 }} value={summaryData.sem2Grades?.[s.id] || ''} onChange={(e) => setSummaryData({...summaryData, sem2Grades: {...summaryData.sem2Grades, [s.id]: e.target.value}})}>
                                            <MenuItem value="">-</MenuItem>
                                            {GRADE_OPTIONS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                                        </TextField>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Grid>
            );
        }

        if (fmtC || fmtD) {
            // Format C/D: Numeric subjects get marks inputs, grade-only subjects get grade dropdowns
            // For classes 5 & 8 (fmtD): hide Sem 1 column, show only Sem 2
            const hideSem1 = fmtD;
            return (
                <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom fontWeight={700}>Subject-wise {hideSem1 ? 'Semester 2' : 'Semester'} Marks / Grades</Typography>
                    <Table size="small">
                        <TableHead><TableRow><TableCell>Subject</TableCell>{!hideSem1 && <TableCell align="center">Sem 1</TableCell>}<TableCell align="center">Sem 2</TableCell></TableRow></TableHead>
                        <TableBody>
                            {subjects.map(s => {
                                const isGradeOnly = Number(s.totalMarks) === 0;
                                return (
                                    <TableRow key={s.id}>
                                        <TableCell>{s.name} {isGradeOnly && <Chip label="Grade" size="small" variant="outlined" />}</TableCell>
                                        {!hideSem1 && <TableCell align="center">
                                            {isGradeOnly ? (
                                                <TextField select size="small" sx={{ width: 90 }} value={summaryData.sem1Grades?.[s.id] || ''} onChange={(e) => setSummaryData({...summaryData, sem1Grades: {...summaryData.sem1Grades, [s.id]: e.target.value}})}>
                                                    <MenuItem value="">-</MenuItem>
                                                    {GRADE_OPTIONS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                                                </TextField>
                                            ) : (
                                                <TextField size="small" type="number" sx={{ width: 90 }} value={summaryData.sem1Grades?.[s.id] || ''} 
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        const maxMark = Number(s.totalMarks) || 100;
                                                        if (val !== '' && Number(val) > maxMark) {
                                                            return;
                                                        }
                                                        setSummaryData({...summaryData, sem1Grades: {...summaryData.sem1Grades, [s.id]: val}});
                                                    }}
                                                    inputProps={{ min: 0, max: Number(s.totalMarks) || 100 }}
                                                    helperText={`Max: ${Number(s.totalMarks) || 100}`}
                                                />
                                            )}
                                        </TableCell>}
                                        <TableCell align="center">
                                            {isGradeOnly ? (
                                                <TextField select size="small" sx={{ width: 90 }} value={summaryData.sem2Grades?.[s.id] || ''} onChange={(e) => setSummaryData({...summaryData, sem2Grades: {...summaryData.sem2Grades, [s.id]: e.target.value}})}>
                                                    <MenuItem value="">-</MenuItem>
                                                    {GRADE_OPTIONS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                                                </TextField>
                                            ) : (
                                                <TextField size="small" type="number" sx={{ width: 90 }} value={summaryData.sem2Grades?.[s.id] || ''} 
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        const maxMark = Number(s.totalMarks) || 100;
                                                        if (val !== '' && Number(val) > maxMark) {
                                                            return; // Don't allow marks above max
                                                        }
                                                        setSummaryData({...summaryData, sem2Grades: {...summaryData.sem2Grades, [s.id]: val}});
                                                    }}
                                                    inputProps={{ min: 0, max: Number(s.totalMarks) || 100 }}
                                                    helperText={`Max: ${Number(s.totalMarks) || 100}`}
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Grid>
            );
        }

        // Format B or fallback: standard grade-only entry (existing)
        return (
            <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom fontWeight={700}>Subject-wise Grades (for grade-only subjects)</Typography>
                <Table size="small">
                    <TableHead><TableRow><TableCell>Subject</TableCell><TableCell align="center">Sem 1 Grade</TableCell><TableCell align="center">Sem 2 Grade</TableCell></TableRow></TableHead>
                    <TableBody>
                        {subjects.map(s => (
                            <TableRow key={s.id}>
                                <TableCell>{s.name}</TableCell>
                                <TableCell align="center">
                                    <TextField size="small" sx={{ width: 80 }} value={summaryData.sem1Grades?.[s.id] || ''} onChange={(e) => setSummaryData({...summaryData, sem1Grades: {...summaryData.sem1Grades, [s.id]: e.target.value}})} />
                                </TableCell>
                                <TableCell align="center">
                                    <TextField size="small" sx={{ width: 80 }} value={summaryData.sem2Grades?.[s.id] || ''} onChange={(e) => setSummaryData({...summaryData, sem2Grades: {...summaryData.sem2Grades, [s.id]: e.target.value}})} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Grid>
        );
    };


    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight={700}>Exam Result & Report Card</Typography>
                <Tabs value={tab} onChange={(e, v) => setTab(v)}>
                    <Tab label="Marks & Summary Entry" />
                    <Tab label="Print Preview" />
                </Tabs>
            </Box>

            {tab === 0 ? (
                <Box>
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={2}>
                                    <TextField select fullWidth size="small" label="Select Exam" value={selectedExam} onChange={(e) => setSelectedExam(e.target.value)}>
                                        <MenuItem value="">-- Select --</MenuItem>
                                        {exams.map(e => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <TextField select fullWidth size="small" label="Select Class" value={selectedClass} onChange={(e) => { setSelectedClass(e.target.value); setSelectedStudent(null); }}>
                                        <MenuItem value="">-- Select --</MenuItem>
                                        {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <TextField select fullWidth size="small" label="Select Division" value={selectedSection} onChange={(e) => setSelectedSection(e.target.value)} disabled={!selectedClass}>
                                        <MenuItem value="">-- Select --</MenuItem>
                                        {sections.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Autocomplete
                                        options={students}
                                        getOptionLabel={(opt) => `${opt.rollNumber || 'N/A'} - ${opt.fullName}`}
                                        value={selectedStudent}
                                        onChange={(e, v) => setSelectedStudent(v)}
                                        size="small"
                                        renderInput={(params) => <TextField {...params} label="Select Student (Optional)" />}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={1}>
                                    <Button variant="contained" fullWidth onClick={handleFetchResults} disabled={loading}><SearchIcon /></Button>
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <Button variant="outlined" color="primary" fullWidth onClick={handleExportExcel} disabled={allResults.length === 0}>Export Excel</Button>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {selectedStudent && allResults.length > 0 && (() => {
                        const className = getClassName();
                        const fmtA = isFormatA(className);
                        const fmtB = isFormatB(className);
                        const fmtC = isFormatC(className);
                        const fmtD = isFormatD(className);

                        // Load current summary data for this student from allResults
                        const studentResult = allResults.find(r => r.student.id === selectedStudent.id);
                        const currentSem1 = studentResult?.summary?.sem1Grades || summaryData.sem1Grades || {};
                        const currentSem2 = studentResult?.summary?.sem2Grades || summaryData.sem2Grades || {};

                        return (
                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        {selectedStudent.fullName} (Roll: {selectedStudent.rollNumber || 'N/A'})
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        {fmtB && marksData.length > 0 && <Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={handleSaveMarks}>Save Marks</Button>}
                                        <Button variant="contained" color="secondary" size="small" startIcon={<AssignmentIcon />} onClick={() => handleOpenSummary(selectedStudent)}>
                                            {fmtB ? 'Edit Summary' : 'Edit Grades & Summary'}
                                        </Button>
                                    </Box>
                                </Box>
                                {/* No subjects configured message */}
                                {subjects.length === 0 && (
                                    <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#fff3e0', borderRadius: 2 }}>
                                        <Typography variant="body1" color="warning.main" fontWeight={600}>
                                            ⚠️ No subjects configured for this class. Please go to Subject Allocation to add subjects first.
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            You can still use "Edit Grades & Summary" to manually enter attendance and other details.
                                        </Typography>
                                    </Box>
                                )}

                                {/* FORMAT A: Grade-only entry (Class 1-4, 6, 7) */}
                                {fmtA && subjects.length > 0 && (
                                    <Box>
                                        <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                            <Chip label="Grade Entry" color="primary" size="small" />
                                            <Typography variant="body2" color="text.secondary">
                                                Enter grades for each subject per semester. Use "Edit Grades & Summary" to save.
                                            </Typography>
                                        </Box>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                                                        <TableCell sx={{ fontWeight: 700 }}>Sr.</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                                                        <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#c8e6c9', minWidth: 120 }}>
                                                            📗 Semester 1 Grade
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#fff9c4', minWidth: 120 }}>
                                                            📘 Semester 2 Grade
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ fontWeight: 700 }}>Result</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {subjects.map((sub, idx) => {
                                                        const g1 = currentSem1[sub.id] || '';
                                                        const g2 = currentSem2[sub.id] || '';
                                                        const pass = (!g1 && !g2) ? '-' : (isGradePass(g1) && isGradePass(g2)) ? '✅ Pass' : '❌ Fail';
                                                        return (
                                                            <TableRow key={sub.id} hover>
                                                                <TableCell>{idx + 1}</TableCell>
                                                                <TableCell sx={{ fontWeight: 500 }}>{sub.name}</TableCell>
                                                                <TableCell align="center" sx={{ bgcolor: '#f1f8e9' }}>
                                                                    <Chip label={g1 || '—'} size="small" variant={g1 ? 'filled' : 'outlined'} color={g1 && !isGradePass(g1) ? 'error' : 'default'} />
                                                                </TableCell>
                                                                <TableCell align="center" sx={{ bgcolor: '#fffde7' }}>
                                                                    <Chip label={g2 || '—'} size="small" variant={g2 ? 'filled' : 'outlined'} color={g2 && !isGradePass(g2) ? 'error' : 'default'} />
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Typography variant="body2" fontWeight={600}>{pass}</Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                )}

                                {/* FORMAT C & D: Marks + Grades hybrid (Class 5, 8, 9, 10) */}
                                {(fmtC || fmtD) && subjects.length > 0 && (
                                    <Box>
                                        <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                            <Chip label="Marks & Grades Entry" color="primary" size="small" />
                                            <Typography variant="body2" color="text.secondary">
                                                {fmtD ? 'Enter Semester 2 marks/grades. Use "Edit Grades & Summary" to save.' : 'Enter marks/grades per semester. Use "Edit Grades & Summary" to save.'}
                                            </Typography>
                                        </Box>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                                                        <TableCell sx={{ fontWeight: 700 }}>Sr.</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                                                        <TableCell align="center" sx={{ fontWeight: 700 }}>Type</TableCell>
                                                        {!fmtD && <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#c8e6c9', minWidth: 110 }}>
                                                            📗 Semester 1
                                                        </TableCell>}
                                                        <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#fff9c4', minWidth: 110 }}>
                                                            📘 Semester 2
                                                        </TableCell>
                                                        {!fmtD && <TableCell align="center" sx={{ fontWeight: 700 }}>Average</TableCell>}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {subjects.map((sub, idx) => {
                                                        const isGradeOnly = Number(sub.totalMarks) === 0;
                                                        const v1 = currentSem1[sub.id] || '';
                                                        const v2 = currentSem2[sub.id] || '';
                                                        let avg = '';
                                                        if (!fmtD && !isGradeOnly && v1 && v2) {
                                                            avg = ((Number(v1) + Number(v2)) / 2).toFixed(0);
                                                        }
                                                        return (
                                                            <TableRow key={sub.id} hover>
                                                                <TableCell>{idx + 1}</TableCell>
                                                                <TableCell sx={{ fontWeight: 500 }}>{sub.name}</TableCell>
                                                                <TableCell align="center">
                                                                    <Chip label={isGradeOnly ? 'Grade' : 'Marks'} size="small" variant="outlined" color={isGradeOnly ? 'warning' : 'info'} />
                                                                </TableCell>
                                                                {!fmtD && <TableCell align="center" sx={{ bgcolor: '#f1f8e9' }}>
                                                                    {isGradeOnly ? (
                                                                        <Chip label={v1 || '—'} size="small" variant={v1 ? 'filled' : 'outlined'} color={v1 && !isGradePass(v1) ? 'error' : 'default'} />
                                                                    ) : (
                                                                        <Typography variant="body2" fontWeight={600}>{v1 || '—'}</Typography>
                                                                    )}
                                                                </TableCell>}
                                                                <TableCell align="center" sx={{ bgcolor: '#fffde7' }}>
                                                                    {isGradeOnly ? (
                                                                        <Chip label={v2 || '—'} size="small" variant={v2 ? 'filled' : 'outlined'} color={v2 && !isGradePass(v2) ? 'error' : 'default'} />
                                                                    ) : (
                                                                        <Typography variant="body2" fontWeight={600}>{v2 || '—'}</Typography>
                                                                    )}
                                                                </TableCell>
                                                                {!fmtD && <TableCell align="center">
                                                                    <Typography variant="body2" fontWeight={700}>{isGradeOnly ? '' : avg || '—'}</Typography>
                                                                </TableCell>}
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                )}

                                {/* FORMAT B: Higher Secondary single marks entry */}
                                {fmtB && marksData.length > 0 && (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: '#eee' }}>
                                                    <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 700 }}>Max Marks</TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 700 }}>Marks Obtained</TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 700 }}>Grade (if applicable)</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {marksData.map((row, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell>{row.subjectName}</TableCell>
                                                        <TableCell align="center">{(row.isGradeOnly || row.maxMarks === 'NA' || Number(row.maxMarks) === 0) ? 'NA' : row.maxMarks}</TableCell>
                                                        <TableCell align="center">
                                                            {!(row.isGradeOnly || row.maxMarks === 'NA' || Number(row.maxMarks) === 0) && <TextField size="small" type="number" sx={{ width: 80 }} value={row.marksObtained} onChange={(e) => handleMarksChange(idx, 'marksObtained', e.target.value)} />}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {(row.isGradeOnly || row.maxMarks === 'NA' || Number(row.maxMarks) === 0) && (
                                                                <TextField select size="small" sx={{ width: 80 }} value={row.grade} onChange={(e) => handleMarksChange(idx, 'grade', e.target.value)}>
                                                                    {GRADE_OPTIONS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                                                                </TextField>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </CardContent>
                        </Card>
                        );
                    })()}

                    {allResults.length > 0 && (
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead><TableRow sx={{ bgcolor: '#e3f2fd' }}><TableCell>Roll No.</TableCell><TableCell>Student Name</TableCell><TableCell align="center">Total/Obtained</TableCell><TableCell align="center">Percentage</TableCell><TableCell align="center">Actions</TableCell></TableRow></TableHead>
                                <TableBody>
                                    {allResults.map(sr => (
                                        <TableRow key={sr.student.id}>
                                            <TableCell>{sr.student.rollNumber}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{sr.student.fullName}</TableCell>
                                            <TableCell align="center">{sr.summary.totalMarks} / {sr.summary.totalObtained}</TableCell>
                                            <TableCell align="center"><Chip label={`${sr.summary.percentage}%`} size="small" color="primary" /></TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Summary Entry"><IconButton size="small" color="secondary" onClick={() => handleOpenSummary(sr.student)}><AssignmentIcon /></IconButton></Tooltip>
                                                <Tooltip title="Preview & Print"><IconButton size="small" color="primary" onClick={() => { setSelectedStudent(students.find(s => s.id === sr.student.id)); setTab(1); handleFetchResults(); }}><PrintIcon /></IconButton></Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            ) : renderPrintPreviewTab()}

            <Dialog open={openSummaryDialog} onClose={() => setOpenSummaryDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Report Card Summary & Grades</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" gutterBottom fontWeight={700}>Term 1 Attendance</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField label="Working Days" size="small" type="number" fullWidth value={summaryData.term1WorkingDays} onChange={(e) => setSummaryData({...summaryData, term1WorkingDays: e.target.value})} />
                                <TextField label="Present Days" size="small" type="number" fullWidth value={summaryData.term1PresentDays} onChange={(e) => setSummaryData({...summaryData, term1PresentDays: e.target.value})} />
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" gutterBottom fontWeight={700}>Term 2 Attendance</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField label="Working Days" size="small" type="number" fullWidth value={summaryData.term2WorkingDays} onChange={(e) => setSummaryData({...summaryData, term2WorkingDays: e.target.value})} />
                                <TextField label="Present Days" size="small" type="number" fullWidth value={summaryData.term2PresentDays} onChange={(e) => setSummaryData({...summaryData, term2PresentDays: e.target.value})} />
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField label="Promoted to Class" size="small" fullWidth sx={{ mt: 2 }} value={summaryData.promotedToClass} onChange={(e) => setSummaryData({...summaryData, promotedToClass: e.target.value})} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField label="School Reopens On" size="small" fullWidth sx={{ mt: 2 }} value={summaryData.reopenDate} onChange={(e) => setSummaryData({...summaryData, reopenDate: e.target.value})} />
                        </Grid>
                        
                        {renderSummaryGradesSection()}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenSummaryDialog(false)}>Cancel</Button>
                    <Button variant="contained" disabled={isSavingSummary} onClick={handleSaveSummary}>
                        {isSavingSummary ? <CircularProgress size={24} /> : 'Save Summary'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ExamResultPrint;
